import React, { useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { Paper, Tooltip } from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'

import { parse } from 'qs'
import { GridRow, GridCell } from '@mui/x-data-grid'

import { guildsTableStyles } from './guilds-table.styles'
import { TableUrlPagination } from '../../../../ui'
import { GUILDS_TABLE_COLUMNS, GUILD_TABLE_STATES } from '../../constants'
import { useGuildsContext } from '../../contexts'
import { DEFAULT_QUERY } from '../../reducers'
import { TableActions } from '../table-actions'
import { useCombos } from '../../../combos'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const GuildsTable = withStyles(guildsTableStyles)(({ classes }) => {
  const { formatMessage, formatDate } = useIntl()
  const combos = useCombos(['tipo_mercado'])
  const navigate = useNavigate()
  const location = useLocation()
  const {
    data,
    pagination: { total_solicitudes: totalEntries },
    loading,
    search,
    getTableData,
  } = useGuildsContext()
  const global = useSelector((state) => state.global)

  const handleGoToDetail = (_, row) => {
    const props = {
      pathname: `/associates/${row.empresa_id}`,
      search: row.id ? `?ca=${row.id}` : '',
    }
    return navigate(props)
  }

  useCombos(['cod_zona_zeus', 'provincias'])

  useEffect(() => {
    const urlSearchParams = parse(location.search.split('?')[1])
    const searchParams = {
      ...DEFAULT_QUERY,
      ...urlSearchParams,
    }
    getTableData(searchParams, global.gremio_id)
  }, [location.search]) // eslint-disable-line react-hooks/exhaustive-deps

  //Formatted columns:
  const formattedColumns = useMemo(() => {
    const columns = [...GUILDS_TABLE_COLUMNS]

    const actionColumn = {
      field: 'actions',
      width: 110,
      align: 'center',
      sorting: false,
    }
    columns.push(actionColumn)

    return columns.map((column) => {
      // Base column configuration for Material React Table
      const baseColumn = {
        accessorKey: column.field,
        id: column.field,
        size: column.width,
        minSize: column.minWidth || column.width,
        maxSize: column.maxWidth,
        enableSorting: column.sorting !== false,
        muiTableHeadCellProps: {
          align: column.align || 'left',
        },
        muiTableBodyCellProps: {
          align: column.align || 'left',
          sx: {
            minWidth: column.minWidth || column.width,
          },
        },
      }

      // Handle action column
      if (column.field === 'actions') {
        return {
          ...baseColumn,
          header: formatMessage({ id: 'global.actions' }),
          Cell: ({ row }) => <TableActions row={row} />,
        }
      }

      // Handle notification bell column
      if (column.field === 'notificacion_instalador') {
        return {
          ...baseColumn,
          header: '',
          enableSorting: false,
          Cell: ({ cell }) =>
            cell.getValue() ? (
              <Tooltip title="Nuevos instaladores colaboradores">
                <NotificationsActiveIcon color="secondary" fontSize="small" />
              </Tooltip>
            ) : null,
        }
      }

      // Handle regular columns
      const result = {
        ...baseColumn,
        header: formatMessage({ id: `pages.guilds.table.columns.${column.field}.title` }),
      }

      // Add lookup functionality if combo data exists
      if (
        column.hasOwnProperty('comboId') &&
        combos[column.comboId] &&
        combos[column.comboId].data
      ) {
        const lookup = getLookup(combos[column.comboId].data)
        result.Cell = ({ cell }) => {
          const value = cell.getValue()
          return lookup[value] || value || <span>--</span>
        }
      } else {
        // Default cell renderer with empty value handling
        result.Cell = ({ cell }) => {
          const value = cell.getValue()
          return value || <span>--</span>
        }
      }

      return result
    })
  }, [combos, formatMessage]) // eslint-disable-line react-hooks/exhaustive-deps

  //Assembling and adjusting data
  const processedData = useMemo(
    () =>
      data.map((item) => {
        const formattedDates = {}
        const formattedStatus = {}
        GUILDS_TABLE_COLUMNS.forEach((column) => {
          if (column.type === 'date' && item[column.field]) {
            formattedDates[column.field] = formatDate(item[column.field])
          }
          if (column.field === 'aceptacion_gremio') {
            formattedStatus[column.field] = item[column.field]
              ? GUILD_TABLE_STATES.accepted
              : GUILD_TABLE_STATES.pending
          }
          if (column.field === 'gremio_recibe_demandas') {
            formattedStatus[column.field] = item[column.field]
              ? GUILD_TABLE_STATES.yes
              : GUILD_TABLE_STATES.no
          }
        })
        return {
          ...item,
          ...formattedDates,
          ...formattedStatus,
          tableData: {
            ...item.tableData,
          },
        }
      }),
    [data] // eslint-disable-line react-hooks/exhaustive-deps
  )

  //Table configuration
  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      tableLayout: 'fixed',
      fixedColumns: {
        right: 1,
      },
      headerStyle: guildsTableStyles.header,
      draggable: false,
    }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={handleGoToDetail}
    />
  )
})
